import { nectarApi } from '@/features/nectar/api/client'
import { parseMoney } from '@/utils'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'

type SettingsWire = {
  minPayoutUsd: string
  cryptoMinPayoutUsd?: string
  paypalMinPayoutUsd?: string
  paypalPayoutsPaused?: string
  paypalPaymentType?: string
  stripeConnectEnabled?: string
  stripePayoutsPaused?: string
  tremendousEnabled?: string
  tremendousPayoutsPaused?: string
  tremendousMinPayoutUsd?: string
}

export type Settings = {
  minPayoutUsd: number
  cryptoMinPayoutUsd: number
  paypalMinPayoutUsd: number
  paypalPayoutsPaused: boolean
  paypalPaymentType: 'email' | 'oauth'
  stripeConnectEnabled: boolean
  stripePayoutsPaused: boolean
  tremendousEnabled: boolean
  tremendousPayoutsPaused: boolean
  tremendousMinPayoutUsd: number
}

export const useFetchSettings = (): UseQueryResult<Settings> => {
  return useQuery({
    queryKey: ['settings'],
    queryFn: async (): Promise<Settings> => {
      const wire = await nectarApi.get<SettingsWire>('/v1/settings')
      return {
        minPayoutUsd: parseMoney(wire.minPayoutUsd),
        // Per-method mins fall back to the global `minPayoutUsd` when absent (stale cache / older backend), not a hardcoded default (AS1).
        cryptoMinPayoutUsd:
          wire.cryptoMinPayoutUsd != null ? parseMoney(wire.cryptoMinPayoutUsd) : parseMoney(wire.minPayoutUsd),
        paypalMinPayoutUsd:
          wire.paypalMinPayoutUsd != null ? parseMoney(wire.paypalMinPayoutUsd) : parseMoney(wire.minPayoutUsd),
        paypalPayoutsPaused: wire.paypalPayoutsPaused === 'true',
        // Absent or unknown values map to 'email' — the legacy flow stays the default (IV4, AS1).
        paypalPaymentType: wire.paypalPaymentType === 'oauth' ? 'oauth' : 'email',
        stripeConnectEnabled: wire.stripeConnectEnabled === 'true',
        stripePayoutsPaused: wire.stripePayoutsPaused === 'true',
        tremendousEnabled: wire.tremendousEnabled === 'true',
        tremendousPayoutsPaused: wire.tremendousPayoutsPaused === 'true',
        tremendousMinPayoutUsd:
          wire.tremendousMinPayoutUsd != null ? parseMoney(wire.tremendousMinPayoutUsd) : parseMoney(wire.minPayoutUsd)
      }
    },
    staleTime: 300_000
  })
}
